import * as UI from "@iyk/music-ui"
import { FormState, useForm } from "@iyk/react-router/client"
import * as React from "react"
import type { action } from "../HomeRoute.tsx"

export const WaitlistDialog = ({
  open,
  onOpenChange
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) => {
  const { Form, fieldErrors, isSubmitting, state } = useForm<typeof action>()
  const actionSucceeded = FormState.isSucceeded(state)
  const [showSuccessState, setShowSuccessState] = React.useState(false)

  const ignorePasswordManagerAttributes = {
    "data-lpignore": "true",
    "data-1p-ignore": "true",
    "data-bwignore": "true"
  } as const

  React.useEffect(() => {
    if (actionSucceeded && !showSuccessState) {
      setShowSuccessState(true)
    }
  }, [actionSucceeded])

  return (
    <UI.Primitive.Dialog.Root open={open} onOpenChange={onOpenChange}>
      <UI.Primitive.Dialog.Portal>
        <UI.Primitive.Dialog.Backdrop className="fixed inset-0 bg-[rgba(25,25,25,0.8)] backdrop-blur transition-opacity duration-200 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0" />
        <UI.Primitive.Dialog.Popup className="data-[nested-dialog-open]:pointer-events-none group fixed inset-0 z-50 flex flex-col bg-gray-1 overflow-y-auto data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 transition-opacity duration-200 outline-none">
          <div className="fixed w-full top-0 bg-gray-1 z-20 flex items-center justify-between p-4 shrink-0">
            <UI.Icon.IykLogo className="size-7" />
            <UI.Primitive.Dialog.Close
              className={UI.classForButton({
                kind: "ghost",
                size: "sm"
              })}
            >
              <UI.Icon.Close />
            </UI.Primitive.Dialog.Close>
          </div>

          <div className="max-w-md mx-auto w-full px-4 my-auto group-data-[nested-dialog-open]:opacity-50 transition-opacity">
            {showSuccessState ? (
              <>
                <main className="grow flex flex-col items-start gap-6 w-full max-w-xl mx-auto sm:justify-center px-4">
                  <div className="flex flex-col">
                    <h2>You're on the list!</h2>
                    <p className="text-gray-11">
                      We will contact you via email when we are ready to launch your world.
                    </p>
                  </div>
                  <button
                    className={UI.classForLink({ className: "group" })}
                    onClick={() => {
                      setShowSuccessState(false)
                    }}
                  >
                    Add another artist{" "}
                    <UI.Icon.ChevronRight className="group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </main>
              </>
            ) : (
              <div className="flex flex-col gap-6 my-4 sm:mt-0">
                <h2 className="text-lg">Join the waitlist</h2>
                <Form method="post">
                  <UI.Fieldset>
                    <UI.TextField
                      autoFocus
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="Email"
                      required
                      validationMessage={fieldErrors?.email}
                      {...ignorePasswordManagerAttributes}
                    />
                    <UI.TextField
                      name="name"
                      label="Artist name"
                      type="text"
                      placeholder="Artist name"
                      required
                      validationMessage={fieldErrors?.name}
                      {...ignorePasswordManagerAttributes}
                    />
                    <UI.TextField
                      name="instagramUrl"
                      label="Instagram handle"
                      type="text"
                      placeholder="Instagram handle"
                      required
                      affix={{ type: "prefix", text: "@" }}
                      validationMessage={fieldErrors?.instagramUrl}
                      {...ignorePasswordManagerAttributes}
                    />

                    <UI.UrlField
                      name="spotifyUrl"
                      label="Spotify URL"
                      placeholder="Spotify URL"
                      required
                      validationMessage={fieldErrors?.spotifyUrl}
                      {...ignorePasswordManagerAttributes}
                    />
                    <UI.UrlField
                      name="bandsintownUrl"
                      label="Bandsintown URL"
                      placeholder="Bandsintown URL"
                      validationMessage={fieldErrors?.bandsintownUrl}
                      {...ignorePasswordManagerAttributes}
                    />
                    <UI.TextField
                      name="label"
                      label="Record label or management"
                      type="text"
                      placeholder="Record label or management"
                      validationMessage={fieldErrors?.label}
                      {...ignorePasswordManagerAttributes}
                    />
                    <span className="hidden" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        id="website"
                        name="website"
                        autoComplete="off"
                        tabIndex={-1}
                        type="text"
                        placeholder="Website URL"
                        {...ignorePasswordManagerAttributes}
                      />
                    </span>
                    <UI.Field
                      name="interests"
                      validationMessage={
                        Array.isArray(fieldErrors?.interests)
                          ? fieldErrors.interests.join(", ")
                          : fieldErrors?.interests
                      }
                      className="gap-4"
                    >
                      <div>
                        <UI.Label>Interested in</UI.Label>
                        <div className="text-xs text-gray-11">
                          Select all that apply.{" "}
                          <UI.Dialog.Root>
                            <UI.Dialog.Trigger className="border-b border-gray-7 hover:border-gray-11 transition-colors">
                              Learn more
                            </UI.Dialog.Trigger>
                            <UI.Dialog.Content className="flex flex-col gap-4">
                              {dialogItems.map((dialogItem, index) => (
                                <DialogItem
                                  key={index}
                                  title={dialogItem.title}
                                  description={dialogItem.description}
                                />
                              ))}
                            </UI.Dialog.Content>
                          </UI.Dialog.Root>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <UI.CheckboxField id="charting" name="interests[]" value="charting">
                          Charting products
                        </UI.CheckboxField>
                        <UI.CheckboxField id="chipped-merch" name="interests[]" value="chipped-merch">
                          Chipped merch
                        </UI.CheckboxField>
                        <UI.CheckboxField id="show-check-ins" name="interests[]" value="show-check-ins">
                          Show check-ins
                        </UI.CheckboxField>
                        <UI.CheckboxField id="chipped-vip" name="interests[]" value="chipped-vip">
                          Chipped VIP
                        </UI.CheckboxField>
                      </div>
                      <UI.Validity />
                    </UI.Field>

                    <UI.Button type="submit" kind="solid" size="md" disabled={isSubmitting}>
                      Submit
                    </UI.Button>
                  </UI.Fieldset>
                </Form>
              </div>
            )}
          </div>
        </UI.Primitive.Dialog.Popup>
      </UI.Primitive.Dialog.Portal>
    </UI.Primitive.Dialog.Root>
  )
}

const DialogItem = ({ title, description }: { title: string; description: string }) => {
  return (
    <div className="flex flex-col gap-0.5 text-sm">
      <h3>{title}</h3>
      <p className="text-gray-11">{description}</p>
    </div>
  )
}

const dialogItems: Array<{ title: string; description: string }> = [
  {
    title: "Chipped merch",
    description:
      "Turn every item you sell into an interactive entry point that fans can claim and keep connected to your world."
  },
  {
    title: "Chipped VIP",
    description: "Enhance your VIP Packages with a chipped item that unlocks special tour moments."
  },
  {
    title: "Charting products (UK and Ireland)",
    description:
      "Use chipped products to create a new physical album format, turning taps into chart-eligible album downloads."
  },
  {
    title: "Show check-ins",
    description:
      "Verify attendance via QR scan, turning each show into a moment to capture first-party data and enhance the fan experience."
  }
]
